import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Crown, UserCircle, UserMinus, ArrowRightLeft, UserPlus, Loader2, Shield } from "lucide-react";

function initials(name: string) {
  return (name ?? "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
}

interface LeadershipPanelProps {
  assocId: string;
  currentUserId: string | null;
  isPresident: boolean;
  isAdmin?: boolean;
}

export function LeadershipPanel({ assocId, currentUserId, isPresident, isAdmin = false }: LeadershipPanelProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const canManage = isPresident || isAdmin;

  const [assignModal, setAssignModal]       = useState<{ positionId: string; title: string } | null>(null);
  const [assignMemberId, setAssignMemberId] = useState("");
  const [transferModal, setTransferModal]   = useState(false);
  const [transferMemberId, setTransferMemberId] = useState("");
  const [vacateId, setVacateId]             = useState<string | null>(null);

  // ── Positions (ordered by rank) ───────────────────────────────────────────
  const { data: positions = [] } = useQuery({
    queryKey: ["assoc-positions", assocId],
    enabled: !!assocId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_positions" as any)
        .select("*")
        .eq("association_id", assocId)
        .eq("is_active", true)
        .order("rank") as any);
      return (data ?? []) as any[];
    },
  });

  // ── Current leadership ────────────────────────────────────────────────────
  const { data: leadership = [], refetch } = useQuery({
    queryKey: ["assoc-leadership", assocId],
    enabled: !!assocId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_leadership" as any)
        .select(`
          id, member_id, position_id, tenure_start,
          association_positions(title, rank),
          association_members!inner(journalist_profiles(full_name, email, avatar_url))
        `)
        .eq("association_id", assocId)
        .eq("is_current", true) as any);
      return (data ?? []) as any[];
    },
  });

  // ── Active members (for assign selector) ─────────────────────────────────
  const { data: activeMembers = [] } = useQuery({
    queryKey: ["assoc-active-members", assocId],
    enabled: canManage && !!assocId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_members" as any)
        .select("id, journalist_profiles(full_name, email)")
        .eq("association_id", assocId)
        .eq("status", "active") as any);
      return (data ?? []) as any[];
    },
  });

  // Build position → leadership row map
  const filled: Record<string, any> = {};
  leadership.forEach((l: any) => { filled[l.position_id] = l; });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const assignMutation = useMutation({
    mutationFn: async ({ positionId, memberId }: { positionId: string; memberId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await (supabase
        .from("association_leadership" as any)
        .insert({
          association_id:  assocId,
          position_id:     positionId,
          member_id:       memberId,
          user_id:         user.id, // Note: should be journalist_id; simplified here
          assigned_by:     user.id,
          assignment_type: "president_assign",
        }) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Position assigned." });
      refetch();
      setAssignModal(null);
      setAssignMemberId("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const vacateMutation = useMutation({
    mutationFn: async (leadershipId: string) => {
      const { error } = await (supabase
        .from("association_leadership" as any)
        .update({ is_current: false, tenure_end: new Date().toISOString().split("T")[0], vacated_reason: "removed" })
        .eq("id", leadershipId) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Position vacated." });
      refetch();
      setVacateId(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const transferMutation = useMutation({
    mutationFn: async (newMemberId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await (supabase.rpc("transfer_association_presidency" as any, {
        p_association_id:  assocId,
        p_new_member_id:   newMemberId,
        p_assigned_by:     user.id,
        p_assignment_type: isAdmin ? "superadmin_assign" : "president_transfer",
      }) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Presidency transferred.", description: "All existing positions have been vacated." });
      refetch();
      qc.invalidateQueries({ queryKey: ["assoc-leadership", assocId] });
      setTransferModal(false);
      setTransferMemberId("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const president = leadership.find((l: any) => l.association_positions?.title === "President");

  return (
    <div className="space-y-4">
      {/* Transfer presidency button — president or admin */}
      {canManage && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
            onClick={() => setTransferModal(true)}
          >
            <ArrowRightLeft className="w-4 h-4" />
            Transfer Presidency
          </Button>
        </div>
      )}

      {/* Position cards */}
      <div className="grid gap-3">
        {positions.map((pos: any) => {
          const holder = filled[pos.id];
          const jp = holder?.association_members?.journalist_profiles;
          const name = jp?.full_name ?? null;
          const isPresPos = pos.title === "President";

          return (
            <Card key={pos.id} className={isPresPos ? "border-[#C8A84B] bg-[#0D2240]/5" : ""}>
              <CardContent className="p-4 flex items-center gap-4">
                {/* Avatar or vacancy */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                  isPresPos
                    ? "bg-[#0D2240] text-[#F0E4B8]"
                    : holder ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground border border-dashed border-muted-foreground/30"
                }`}>
                  {holder
                    ? (jp?.avatar_url ? <img src={jp.avatar_url} className="w-full h-full rounded-full object-cover" alt="" /> : initials(name ?? ""))
                    : <UserCircle className="w-5 h-5 opacity-40" />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{pos.title}</p>
                    {isPresPos && <Crown className="w-3.5 h-3.5 text-[#C8A84B]" />}
                    {!holder && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground border-dashed">Vacant</Badge>
                    )}
                  </div>
                  {holder ? (
                    <>
                      <p className="text-sm text-foreground font-medium">{name ?? "Unknown"}</p>
                      <p className="text-xs text-muted-foreground">
                        {jp?.email} · Since {new Date(holder.tenure_start).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No one assigned yet</p>
                  )}
                </div>

                {/* Actions */}
                {canManage && !isPresPos && (
                  <div className="flex gap-1.5 shrink-0">
                    {!holder ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => { setAssignModal({ positionId: pos.id, title: pos.title }); setAssignMemberId(""); }}
                      >
                        <UserPlus className="w-3 h-3" /> Assign
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setVacateId(holder.id)}
                      >
                        <UserMinus className="w-3 h-3" /> Vacate
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {positions.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              No positions configured yet.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Assign modal */}
      <AlertDialog open={!!assignModal} onOpenChange={o => { if (!o) setAssignModal(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign {assignModal?.title}</AlertDialogTitle>
            <AlertDialogDescription>Choose an active member for this position.</AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={assignMemberId} onValueChange={setAssignMemberId}>
            <SelectTrigger>
              <SelectValue placeholder="Select member…" />
            </SelectTrigger>
            <SelectContent>
              {activeMembers.map((m: any) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.journalist_profiles?.full_name ?? "Unknown"} · {m.journalist_profiles?.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!assignMemberId || assignMutation.isPending}
              onClick={() => assignMutation.mutate({ positionId: assignModal!.positionId, memberId: assignMemberId })}
            >
              {assignMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Assign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vacate confirm */}
      <AlertDialog open={!!vacateId} onOpenChange={o => { if (!o) setVacateId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vacate Position</AlertDialogTitle>
            <AlertDialogDescription>This will remove the current holder from this position. The position will become vacant.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => vacateMutation.mutate(vacateId!)}
              disabled={vacateMutation.isPending}
            >
              {vacateMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Vacate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Presidency modal */}
      <AlertDialog open={transferModal} onOpenChange={o => { if (!o) setTransferModal(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#C8A84B]" /> Transfer Presidency
            </AlertDialogTitle>
            <AlertDialogDescription>
              Selecting a new president will <strong>vacate all current leadership positions</strong>. The new president must assemble their own team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={transferMemberId} onValueChange={setTransferMemberId}>
            <SelectTrigger>
              <SelectValue placeholder="Select new president…" />
            </SelectTrigger>
            <SelectContent>
              {activeMembers
                .filter((m: any) => !president || m.id !== president.member_id)
                .map((m: any) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.journalist_profiles?.full_name ?? "Unknown"} · {m.journalist_profiles?.email}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#0D2240] hover:bg-[#1a3a60]"
              disabled={!transferMemberId || transferMutation.isPending}
              onClick={() => transferMutation.mutate(transferMemberId)}
            >
              {transferMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Confirm Transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
